/**
 * Tiny, self-contained Markdown to HTML renderer for Lumio's assistant replies.
 *
 * Why hand-rolled: the repo ships no markdown dependency, so a small subset
 * renderer is enough and avoids pulling a parser in. The assistant copy is now
 * REMOTE, untrusted text (the Lumio backend), so this is also the sanitiser. The
 * output string is rendered via a dedicated `styled.div` +
 * `dangerouslySetInnerHTML` at the call site (`MarkdownText`), NOT through
 * `<Text>` (which now escapes), so EVERY branch escapes raw text (including
 * quotes, for the attribute context) BEFORE composing tags, and link hrefs are
 * gated to a safe allow-list. No markup in a reply can inject HTML. Partial
 * markdown arriving mid-stream (an unterminated `**` or code fence) renders
 * best-effort and resolves once the token completes.
 *
 * Supported: headings (`#`…`######`), unordered (`-`/`*`) and ordered (`1.`)
 * lists, blockquotes (`>`), fenced code (```), horizontal rules (`---`),
 * paragraphs, and inline bold, italic, `code`, and [links](url).
 */

// Escapes for BOTH text and attribute contexts: the quotes matter because the
// href below is interpolated into a double-quoted attribute, and a reply is
// remote/untrusted. Run on every raw run BEFORE any tag is composed.
const escapeHtml = (s: string): string =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/** Only allow links we know are safe to open; anything else collapses to `#`.
 * The `/(?!\/)` rejects protocol-relative `//host` URLs (would point off-site)
 * while still allowing internal absolute paths. */
const safeHref = (url: string): string =>
  /^(https?:\/\/|mailto:|\/(?!\/)|#)/i.test(url) ? url : '#';

/**
 * Render inline spans inside one block of text. Inline code is split out FIRST
 * (its contents must not be re-formatted), then the remaining runs are escaped
 * and decorated with links/bold/italic.
 */
const inline = (raw: string): string =>
  raw
    .split(/(`[^`]+`)/g)
    .map((part) => {
      const code = part.match(/^`([^`]+)`$/);
      if (code) return `<code>${escapeHtml(code[1])}</code>`;

      let s = escapeHtml(part);
      s = s.replace(
        /\[([^\]]+)\]\(([^)\s]+)\)/g,
        (_m, text: string, url: string) =>
          `<a href="${safeHref(url)}" target="_blank" rel="noopener noreferrer">${text}</a>`,
      );
      s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
      // Single-asterisk italic, but never a `*` adjacent to another (that was bold).
      s = s.replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, '$1<em>$2</em>');
      s = s.replace(/(^|\s)_([^_\n]+)_(?=\s|$)/g, '$1<em>$2</em>');
      return s;
    })
    .join('');

export const markdownToHtml = (md: string): string => {
  const lines = md.replace(/\r\n/g, '\n').split('\n');
  const out: string[] = [];
  let para: string[] = [];
  let i = 0;

  const flushPara = (): void => {
    if (para.length) {
      out.push(`<p>${para.map(inline).join(' ')}</p>`);
      para = [];
    }
  };

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block: collect verbatim until the closing fence (or the end,
    // for a still-streaming block) and escape as one unit.
    if (/^```/.test(line)) {
      flushPara();
      const code: string[] = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i])) {
        code.push(lines[i]);
        i++;
      }
      if (i < lines.length) i++; // consume the closing fence
      out.push(`<pre><code>${escapeHtml(code.join('\n'))}</code></pre>`);
      continue;
    }

    if (/^\s*$/.test(line)) {
      flushPara();
      i++;
      continue;
    }

    if (/^\s*(---|\*\*\*|___)\s*$/.test(line)) {
      flushPara();
      out.push('<hr>');
      i++;
      continue;
    }

    const heading = line.match(/^(#{1,6})\s+(.*)$/);
    if (heading) {
      flushPara();
      // The panel is narrow, cap headings at h3-ish weight (# becomes h3).
      const level = Math.min(heading[1].length + 2, 6);
      out.push(`<h${level}>${inline(heading[2].trim())}</h${level}>`);
      i++;
      continue;
    }

    if (/^\s*>\s?/.test(line)) {
      flushPara();
      const quote: string[] = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) {
        quote.push(lines[i].replace(/^\s*>\s?/, ''));
        i++;
      }
      out.push(`<blockquote>${quote.map(inline).join('<br>')}</blockquote>`);
      continue;
    }

    if (/^\s*[-*]\s+/.test(line)) {
      flushPara();
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(`<li>${inline(lines[i].replace(/^\s*[-*]\s+/, ''))}</li>`);
        i++;
      }
      out.push(`<ul>${items.join('')}</ul>`);
      continue;
    }

    if (/^\s*\d+\.\s+/.test(line)) {
      flushPara();
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(`<li>${inline(lines[i].replace(/^\s*\d+\.\s+/, ''))}</li>`);
        i++;
      }
      out.push(`<ol>${items.join('')}</ol>`);
      continue;
    }

    para.push(line);
    i++;
  }

  flushPara();
  return out.join('');
};

/** Plain user text → safe HTML (escape + preserve newlines). User input is
 * NEVER treated as markdown: it is escaped here and rendered via the
 * `UserBubble` styled.div's `dangerouslySetInnerHTML` (not `<Text>`). */
export const plainToHtml = (text: string): string =>
  escapeHtml(text).replace(/\n/g, '<br>');
